import { type FormEvent, useId, useState } from 'react';
import type { PhoneNumberType } from '../../hooks/useAdminData';
import { useRegisterOrgPhoneNumber } from '../../hooks/useAdminData';
import { Modal } from '../Modal';

interface RegisterPhoneNumberModalProps {
  open: boolean;
  onClose: () => void;
  orgId: string;
}

const PHONE_TYPES: { value: PhoneNumberType; label: string }[] = [
  { value: 'mobile', label: 'Mobile' },
  { value: 'landline', label: 'Landline' },
  { value: 'toll_free', label: 'Toll-free' },
];

function conflictMessage(error: unknown): string | null {
  const status = (error as { response?: { status?: number } })?.response?.status;
  if (status === 409) return 'This phone number is already registered';
  return null;
}

export function RegisterPhoneNumberModal({ open, onClose, orgId }: RegisterPhoneNumberModalProps) {
  const [phoneE164, setPhoneE164] = useState('');
  const [providerPhoneSid, setProviderPhoneSid] = useState('');
  const [country, setCountry] = useState('');
  const [phoneType, setPhoneType] = useState<PhoneNumberType>('mobile');
  const [regulatoryBundleId, setRegulatoryBundleId] = useState('');
  const [label, setLabel] = useState('');
  const [error, setError] = useState<string | null>(null);

  const registerPhoneNumber = useRegisterOrgPhoneNumber(orgId);

  const phoneE164Id = useId();
  const providerPhoneSidId = useId();
  const countryId = useId();
  const phoneTypeId = useId();
  const regulatoryBundleIdId = useId();
  const labelId = useId();

  function reset() {
    setPhoneE164('');
    setProviderPhoneSid('');
    setCountry('');
    setPhoneType('mobile');
    setRegulatoryBundleId('');
    setLabel('');
    setError(null);
  }

  function handleClose() {
    reset();
    onClose();
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!phoneE164.trim() || !providerPhoneSid.trim() || !country.trim()) return;

    setError(null);

    registerPhoneNumber.mutate(
      {
        phoneE164: phoneE164.trim(),
        providerPhoneSid: providerPhoneSid.trim(),
        country: country.trim(),
        phoneType,
        ...(regulatoryBundleId.trim() && { regulatoryBundleId: regulatoryBundleId.trim() }),
        ...(label.trim() && { label: label.trim() }),
      },
      {
        onSuccess: () => {
          reset();
          onClose();
        },
        onError: (err) => {
          setError(conflictMessage(err) ?? 'Failed to register phone number');
        },
      },
    );
  }

  return (
    <Modal open={open} onClose={handleClose} title="Register Phone Number">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor={phoneE164Id} className="block text-sm font-medium text-text-secondary mb-1">
            Phone Number <span className="text-danger">*</span>
          </label>
          <input
            id={phoneE164Id}
            type="tel"
            value={phoneE164}
            onChange={(e) => setPhoneE164(e.target.value)}
            placeholder="+15551234567"
            className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:border-primary transition-colors"
            required
          />
        </div>

        <div>
          <label
            htmlFor={providerPhoneSidId}
            className="block text-sm font-medium text-text-secondary mb-1"
          >
            ElevenLabs Phone Number Id <span className="text-danger">*</span>
          </label>
          <input
            id={providerPhoneSidId}
            type="text"
            value={providerPhoneSid}
            onChange={(e) => setProviderPhoneSid(e.target.value)}
            placeholder="phnum_..."
            className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:border-primary transition-colors"
            required
          />
          <p className="mt-1 text-xs text-text-muted">
            Must be an already-provisioned ElevenLabs phone number id (starts with{' '}
            <code>phnum_</code>).
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor={countryId} className="block text-sm font-medium text-text-secondary mb-1">
              Country <span className="text-danger">*</span>
            </label>
            <input
              id={countryId}
              type="text"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              placeholder="e.g. US"
              className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:border-primary transition-colors"
              required
            />
          </div>
          <div>
            <label htmlFor={phoneTypeId} className="block text-sm font-medium text-text-secondary mb-1">
              Phone Type
            </label>
            <select
              id={phoneTypeId}
              value={phoneType}
              onChange={(e) => setPhoneType(e.target.value as PhoneNumberType)}
              className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text focus:outline-none focus:border-primary transition-colors"
            >
              {PHONE_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label
            htmlFor={regulatoryBundleIdId}
            className="block text-sm font-medium text-text-secondary mb-1"
          >
            Regulatory Bundle Id
          </label>
          <input
            id={regulatoryBundleIdId}
            type="text"
            value={regulatoryBundleId}
            onChange={(e) => setRegulatoryBundleId(e.target.value)}
            placeholder="Leave empty if not applicable"
            className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:border-primary transition-colors"
          />
          <p className="mt-1 text-xs text-text-muted">
            MX numbers need a regulatory bundle id to be routable.
          </p>
        </div>

        <div>
          <label htmlFor={labelId} className="block text-sm font-medium text-text-secondary mb-1">
            Label
          </label>
          <input
            id={labelId}
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Sales line"
            className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:border-primary transition-colors"
          />
        </div>

        {error && (
          <p className="text-sm rounded-lg px-3 py-2 border bg-danger/10 border-danger/30 text-danger">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-sm rounded-lg border border-border text-text-secondary hover:bg-surface-hover transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={
              !phoneE164.trim() ||
              !providerPhoneSid.trim() ||
              !country.trim() ||
              registerPhoneNumber.isPending
            }
            className="px-4 py-2 text-sm rounded-lg font-medium bg-primary text-background hover:bg-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {registerPhoneNumber.isPending ? 'Registering...' : 'Register Number'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
